import { z } from 'zod';

enum AgentCommand {
	NotifyScrapingStep = 'notify_scraping_step',
	NotifyScrapingFinished = 'notify_scraping_finished',
	OnboardingError = 'onboarding_error',
	SetCreationSettings = 'set_creation_settings',
	ShowCallNotification = 'show_call_notification',
	OnboardingFinished = 'onboarding_finished',
	AgentInfo = 'agent_info',
	FirstCustomerCallStarted = 'first_customer_call_started',
	FirstCustomerCallFinished = 'first_customer_call_finished',
	CallCreationFinished = 'call_creation_finished'
}

enum AgentInfoState {
	Start = 'start',
	Initial = 'initial',
	Parsing = 'parsing',
	Created = 'created'
}

const AgentInfoBody = z.object({
	phone_number: z.string(),
	business_phone: z.string().optional().default(''),
	created_customer_idn: z.string(),
	industry: z.string(),
	business_name: z.string(),
	agent_name: z.string(),
	agent_title: z.string(),
	widget_script: z.string(),
	contact_phone: z.string(),
	contact_fullname: z.string(),
	contact_email: z.string(),
	source: z.string(),
	scraper_type: z.string()
});

const FirstCustomerCallFinishedBody = z.object({
	user_actor_id: z.string(),
	agent_phone_number: z.string(),
	user_phone_number: z.string(),
	customer_idn: z.string()
});

const FirstCustomerCallFinishedTransportArgs = z
	.object({
		user_actor_id: z.string(),
		arguments: z.string()
	})
	.transform((args) => {
		const parsed = JSON.parse(args.arguments);
		return FirstCustomerCallFinishedBody.parse({
			...parsed,
			user_actor_id: args.user_actor_id
		});
	});

export const ArgumentsArray = z
	.array(
		z.object({
			name: z.string(),
			value: z.string()
		})
	)
	.transform((items) => {
		return Object.fromEntries(items.map((i) => [i.name, i.value]));
	});

export const NotificationSchema = z.object({
	act_id: z.string(),
	stream_id: z.string(),
	text: z.string(),
	datetime: z.string(),
	arguments: z.array(
		z.object({
			name: z.string(),
			value: z.string()
		})
	)
});

export const SetCreationSettingsArgs = z.object({
	user_actor_id: z.string(),
	industry: z.string(),
	country_code: z.string(),
	customer_phone: z.string(),
	customer_name: z.string(),
	customer_email: z.string(),
	show_customer_form: z.string().transform((v) => v === 'True'),
	show_consent_form: z.string().transform((v) => v === 'True')
});

export const NotifyScrapingStepArgs = z.object({
	user_actor_id: z.string(),
	question: z.string(),
	answer: z.string(),
	step_number: z.string(),
	max_steps: z.string(),
	stage: z.string()
});

export const NotifyScrapingFinishedArgs = z.object({
	user_actor_id: z.string(),
	business_name: z.string(),
	agent_name: z.string(),
	agent_title: z.string(),
	industry: z.string(),
	country_code: z.string(),
	customer_phone: z.string(),
	customer_name: z.string(),
	customer_email: z.string()
});

export const AgentInfoTransportArgs = z.object({
	user_actor_id: z.string(),
	state: z.nativeEnum(AgentInfoState),
	body: z.string().transform((val) => {
		const fallbackObject = {} as z.infer<typeof AgentInfoBody>;
		try {
			const parsed = JSON.parse(val);
			const result = AgentInfoBody.safeParse(parsed);
			return result.success ? result.data : fallbackObject;
		} catch {
			return fallbackObject;
		}
	})
});

export const OnboardingFinishedArgs = z.object({
	agent_name: z.string(),
	agent_phone_number: z.string().optional().default(''),
	agent_title: z.string(),
	business_phone: z.string().optional().default(''),
	business_name: z.string(),
	created_customer_idn: z.string(),
	email: z.string(),
	industry: z.string(),
	phone_number: z.string().optional().default(''),
	user_actor_id: z.string(),
	webrtc_connector_idn: z.string(),
	widget_script: z.string()
});

export const FirstCustomerCallFinishedArgs = z.union([
	FirstCustomerCallFinishedBody,
	FirstCustomerCallFinishedTransportArgs
]);

export const CallCreationFinishedArgs = z.object({
	user_actor_id: z.string(),
	owner_call_datetime: z.string(),
	customer_name: z.string(),
	agent_name: z.string(),
	agent_phone: z.string()
});

export const GenericArgs = z.record(z.string(), z.string());

export const RawAgentCommand = z.object({
	integration_idn: z.string(),
	connector_idn: z.string(),
	command_idn: z.nativeEnum(AgentCommand),
	act_id: z.string(),
	external_event_id: z.string(),
	arguments: ArgumentsArray
});

export type RawAgentCommand = z.infer<typeof RawAgentCommand>;
export type NotificationPayload = z.infer<typeof NotificationSchema>;
export type CallCreationFinishedPayload = z.infer<
	typeof CallCreationFinishedArgs
>;

export const CommandSchemas = {
	[AgentCommand.SetCreationSettings]: SetCreationSettingsArgs,
	[AgentCommand.NotifyScrapingStep]: NotifyScrapingStepArgs,
	[AgentCommand.NotifyScrapingFinished]: NotifyScrapingFinishedArgs,
	[AgentCommand.OnboardingError]: GenericArgs,
	[AgentCommand.ShowCallNotification]: GenericArgs,
	[AgentCommand.OnboardingFinished]: OnboardingFinishedArgs,
	[AgentCommand.AgentInfo]: AgentInfoTransportArgs,
	[AgentCommand.FirstCustomerCallStarted]: GenericArgs,
	[AgentCommand.FirstCustomerCallFinished]: FirstCustomerCallFinishedArgs,
	[AgentCommand.CallCreationFinished]: CallCreationFinishedArgs
} satisfies {
	[K in AgentCommand]: z.ZodTypeAny;
};

export type CommandPayloadMap = {
	[K in AgentCommand]: z.infer<(typeof CommandSchemas)[K]>;
};

export type CommandHandler<K extends AgentCommand> = (
	payload: CommandPayloadMap[K]
) => void;

export type AgentRealtimeTransportEvents = {
	notification: NotificationPayload;
	command: RawAgentCommand;
	connect: unknown;
	disconnect: unknown;
	connect_error: Error;
	reconnect: unknown;
};

export type Unsubscribe = () => void;

export { AgentCommand, AgentInfoState };
